import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import style from "./PublicEventDetails.module.css";
import { baseurl } from "../Url";
import { IoMdTime } from "react-icons/io";
import { MdOutlinePlace } from "react-icons/md";
import { RiOrganizationChart } from "react-icons/ri";
import Loader from "../Loder/Loader";

const PublicEventDetails = () => {
  const { userId, eventId } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAttendModal, setShowAttendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isUserAttending, setIsUserAttending] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const currentUserId = localStorage.getItem("USERID");
  const isLoggedIn = !!currentUserId;

  const getDateValue = (dateObj) => {
    if (!dateObj) return null;
    if (typeof dateObj === "string") return dateObj;
    return dateObj.$date || dateObj;
  };

  const fetchEventData = async () => {
    if (!eventId) {
      setError("Event not found");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${baseurl}/event/details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_id: eventId }),
      });

      const result = await response.json();

      if (!result.status || !result.data) {
        setError(result.description || "Event not found");
        setLoading(false);
        return;
      }

      setEventData(result.data);

      if (isLoggedIn) {
        const attendeesResponse = await fetch(
          `${baseurl}/event/attendees/byevent`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event_id: eventId }),
          }
        );

        if (attendeesResponse.ok) {
          const attendeesData = await attendeesResponse.json();
          const attendees = attendeesData.data?.[0] || [];
          const userAttendee = attendees.find(
            (a) => a.user_id === currentUserId
          );
          if (userAttendee) {
            localStorage.setItem("attendeeId", userAttendee._id?.$oid || "");
          }
          setIsUserAttending(!!userAttendee);
        }
      }
    } catch (err) {
      setError("Failed to load event");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  // When landing via shared event link (?ref=...), validate referral and store event_id, user_id, referral_code in localStorage
  useEffect(() => {
    if (typeof window === "undefined" || !eventId) return;
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get("ref") || params.get("ref_code") || params.get("code");
    if (!rawCode) return;

    const fetchReferral = async () => {
      try {
        const res = await fetch(`${baseurl}/referral/link?code=${encodeURIComponent(rawCode)}`);
        const data = await res.json();

        if (data && data.status && data.data && data.data.referral_code) {
          localStorage.setItem("duno_referral_code", data.data.referral_code);
          if (data.data.referrer_user_id) {
            localStorage.setItem("duno_referral_user_id", data.data.referrer_user_id);
          } else if (userId) {
            localStorage.setItem("duno_referral_user_id", userId);
          }
          localStorage.setItem("duno_referral_asset_category", data.data.referral_asset_category || "EVENT");
          localStorage.setItem("duno_referral_asset_id", data.data.referral_asset_id || eventId);
          // Explicitly store event_id and user_id for event share context
          localStorage.setItem("duno_referral_event_id", eventId);
          if (userId) localStorage.setItem("duno_referral_event_user_id", userId);
        } else {
          localStorage.removeItem("duno_referral_code");
          localStorage.removeItem("duno_referral_user_id");
          localStorage.removeItem("duno_referral_asset_category");
          localStorage.removeItem("duno_referral_asset_id");
          localStorage.removeItem("duno_referral_event_id");
          localStorage.removeItem("duno_referral_event_user_id");
        }
      } catch (e) {
        console.warn("Failed to capture referral for event:", e);
      }
    };

    fetchReferral();
  }, [eventId, userId]);

  // Record referral visit once for logged-in visitors (non-referrer),
  // so referrals appear in Community tab even before signup/conversion actions.
  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId || !eventId) return;
    const referralCode = localStorage.getItem("duno_referral_code");
    const referrerUserId = localStorage.getItem("duno_referral_user_id");
    const assetCategory = localStorage.getItem("duno_referral_asset_category") || "EVENT";
    const assetId = localStorage.getItem("duno_referral_asset_id") || eventId;

    if (!referralCode || !referrerUserId || !assetId) return;
    if (String(referrerUserId) === String(currentUserId)) return;

    const dedupeKey = `duno_referral_visit_recorded_${referralCode}_${currentUserId}_${assetId}`;
    if (sessionStorage.getItem(dedupeKey) === "1") return;

    fetch(`${baseurl}/referral/record-action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referrer_user_id: referrerUserId,
        referee_id: currentUserId,
        referee_username: localStorage.getItem("username") || localStorage.getItem("email") || "",
        referral_asset_category: assetCategory,
        referral_asset_id: assetId,
        action: "LINK_VISIT",
      }),
    })
      .then(() => {
        sessionStorage.setItem(dedupeKey, "1");
      })
      .catch((err) => {
        console.warn("Failed to record referral visit:", err);
      });
  }, [currentUserId, eventId]);

  const checkUserAndProceed = async (onSuccess) => {
    if (!currentUserId) {
      navigate("/login", {
        state: { from: `/users/events/${userId}/${eventId}` },
      });
      return;
    }
    try {
      const res = await fetch(`${baseurl}/check/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: currentUserId }),
      });
      const data = await res.json();
      if (data.status) {
        onSuccess?.();
      } else {
        navigate("/login", {
          state: { from: `/users/events/${userId}/${eventId}` },
        });
      }
    } catch {
      navigate("/login", {
        state: { from: `/users/events/${userId}/${eventId}` },
      });
    }
  };

  const handleAttendClick = () => {
    checkUserAndProceed(() => setShowAttendModal(true));
  };

  const handleRegisteredClick = () => {
    checkUserAndProceed(() => setShowCancelModal(true));
  };

  const handleConfirmAttend = async () => {
    setShowAttendModal(false);
    await checkUserAndProceed(async () => {
      const userName = localStorage.getItem("username");
      const eventTitle = eventData?.event_title;

      try {
        const response = await fetch(`${baseurl}/event/attendees`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_name: userName,
            user_id: currentUserId,
            event_id: eventId,
            event_title: eventTitle,
          }),
        });

        if (!response.ok) throw new Error("Failed to register");
        setIsUserAttending(true);
        setSnackbarMessage("Event registered successfully");
        setShowSnackbar(true);
        setTimeout(() => {
          setShowSnackbar(false);
          setSnackbarMessage("");
        }, 2000);
        fetchEventData();
      } catch (err) {
        setSnackbarMessage("Failed to register");
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 2000);
      }
    });
  };

  const handleConfirmCancel = async () => {
    setShowCancelModal(false);
    await checkUserAndProceed(async () => {
      const attendeeId = localStorage.getItem("attendeeId");

      try {
        const response = await fetch(`${baseurl}/event/attendees/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: currentUserId,
            attendee_id: attendeeId,
          }),
        });

        if (!response.ok) throw new Error("Failed to cancel");
        setIsUserAttending(false);
        setSnackbarMessage("Event cancelled");
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 2000);
        fetchEventData();
      } catch (err) {
        setSnackbarMessage("Failed to cancel");
        setShowSnackbar(true);
        setTimeout(() => setShowSnackbar(false), 2000);
      }
    });
  };

  if (loading) {
    return (
      <div className={`${style.container} ${style.loadingState}`}>
        <Loader />
      </div>
    );
  }

  if (error || !eventData) {
    return (
      <div className={`${style.container} ${style.errorState}`}>
        <div className={style.errorBox}>
          <p>{error || "Event not found"}</p>
          <button onClick={() => navigate("/")}>Go to Home</button>
        </div>
      </div>
    );
  }

  const startDate = getDateValue(eventData.event_start_date);
  const endDate = getDateValue(eventData.event_end_date);

  return (
    <div className={style.container}>
      <div className={style.content}>
        <div className={style.eventCard}>
          <header className={style.header}>
            <div className={style.headerTop}>
              <div className={style.titleRow}>
                <h1>{eventData.event_title}</h1>
              </div>
              {startDate && endDate && (
                <p className={style.dateRange}>
                  {new Date(startDate).toLocaleDateString()} –{" "}
                  {new Date(endDate).toLocaleDateString()}
                </p>
              )}
              {eventData.event_mode && (
                <p className={style.mode}>Mode: {eventData.event_mode}</p>
              )}
            </div>
          </header>

          <div className={style.mainContent}>
            <div className={style.posterSection}>
              {eventData.event_image && (
                <div className={style.poster}>
                  <img src={eventData.event_image} alt={eventData.event_title} />
                </div>
              )}
              <div className={style.description}>
                <h3>Details</h3>
                {eventData.event_discription && (
                  <h4>{eventData.event_discription}</h4>
                )}
                {eventData.event_details && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: eventData.event_details,
                    }}
                  />
                )}
              </div>
            </div>

            <div className={style.detailCards}>
              {startDate && endDate && (
                <div className={style.detailCard}>
                  <div className={style.cardLabel}>
                    <IoMdTime className={style.icon} />
                    <span>Date & Time</span>
                  </div>
                  <span>
                    {new Date(startDate).toLocaleString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}{" "}
                    –{" "}
                    {new Date(endDate).toLocaleString("en-US", {
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}{" "}
                    IST
                  </span>
                </div>
              )}
              {eventData.host_name && (
                <div className={style.detailCard}>
                  <div className={style.cardLabel}>
                    <RiOrganizationChart size={18} />
                    <span>Hosted By</span>
                  </div>
                  <span>{eventData.host_name}</span>
                </div>
              )}
              {eventData.event_venue && (
                <div className={style.detailCard}>
                  <div className={style.cardLabel}>
                    <MdOutlinePlace size={18} />
                    <span>Venue</span>
                  </div>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: eventData.event_venue,
                    }}
                  />
                </div>
              )}

              <div className={style.attendSection}>
                {isLoggedIn ? (
                  isUserAttending ? (
                    <button
                      className={style.attendingBtn}
                      onClick={handleRegisteredClick}
                    >
                      Registered
                    </button>
                  ) : (
                    <button
                      className={style.attendBtn}
                      onClick={handleAttendClick}
                    >
                      Attend
                    </button>
                  )
                ) : (
                  <button
                    className={style.attendBtnAnonymous}
                    onClick={() =>
                      navigate("/login", {
                        state: { from: `/users/events/${userId}/${eventId}` },
                      })
                    }
                  >
                    Attend
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAttendModal && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <h2>Are you willing to attend this event?</h2>
            <div className={style.modalButtons}>
              <button
                className={style.modalCancel}
                onClick={() => setShowAttendModal(false)}
              >
                Cancel
              </button>
              <button className={style.modalConfirm} onClick={handleConfirmAttend}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <h2>
              You have already registered for this event.
              <br />
              Do you want to cancel?
            </h2>
            <div className={style.modalButtons}>
              <button
                className={style.modalCancel}
                onClick={handleConfirmCancel}
              >
                Yes
              </button>
              <button
                className={style.modalConfirm}
                onClick={() => setShowCancelModal(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showSnackbar && (
        <div className={style.snackbar}>
          <p>{snackbarMessage}</p>
        </div>
      )}
    </div>
  );
};

export default PublicEventDetails;
